import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiFilter, FiChevronLeft, FiChevronRight, FiActivity, FiX } from 'react-icons/fi';
import { GitBranch, Search as LSearch, Award } from 'lucide-react';
import { toast } from 'react-toastify';
import useAdminDashboardStore from '@/stores/AdminStore/AdminDashboardStore';
import { getOrganizationUsers, User, UsersPagination } from '@/services/PagesServices/AdminDashboardService';
import { setReportsTo, getUserDirectory, User as DirectoryUser } from '@/services/PagesServices/UsersService';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';
import './OrganizationUsers.css';

const OrganizationUsers: React.FC = () => {
  const navigate = useNavigate();
  const {
    selectedOrganization,
    organizations,
    fetchOrganizations
  } = useAdminDashboardStore();

  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState<UsersPagination>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<number | null>(null);

  // Reports-to picker state
  const [reportsToTarget, setReportsToTarget] = useState<User | null>(null);
  const [reportsToSearch, setReportsToSearch] = useState('');
  const [reportsToCandidates, setReportsToCandidates] = useState<DirectoryUser[]>([]);
  const [reportsToLoading, setReportsToLoading] = useState(false);
  const [reportsToSaving, setReportsToSaving] = useState(false);

  useEffect(() => {
    if (!reportsToTarget) return;
    const t = setTimeout(async () => {
      setReportsToLoading(true);
      try {
        const res = await getUserDirectory({ search: reportsToSearch, page: 1, limit: 25 });
        if (res.success && res.data) {
          setReportsToCandidates(res.data.filter((u) => u.id !== reportsToTarget.id));
        }
      } finally {
        setReportsToLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [reportsToTarget, reportsToSearch]);

  const openReportsToPicker = (user: User) => {
    setReportsToTarget(user);
    setReportsToSearch('');
    setReportsToCandidates([]);
  };

  const closeReportsToPicker = () => {
    setReportsToTarget(null);
    setReportsToSearch('');
    setReportsToCandidates([]);
  };

  const handlePickReportsTo = async (managerId: string | null, managerName: string | null) => {
    if (!reportsToTarget) return;
    setReportsToSaving(true);
    try {
      const res = await setReportsTo(reportsToTarget.id, managerId);
      if (res.success) {
        const targetName =
          reportsToTarget.first_name || reportsToTarget.last_name
            ? `${reportsToTarget.first_name} ${reportsToTarget.last_name}`.trim()
            : reportsToTarget.email;
        toast.success(
          managerId
            ? `${targetName} now reports to ${managerName}`
            : `Cleared reports-to for ${targetName}`
        );
        closeReportsToPicker();
        await fetchUsers();
      } else {
        toast.error(res.error?.message || 'Failed to update reports-to');
      }
    } finally {
      setReportsToSaving(false);
    }
  };

  // Fetch organizations on mount if not already loaded
  useEffect(() => {
    if (!organizations || organizations.length === 0) {
      fetchOrganizations();
    }
  }, []);

  // Fetch users when organization or filters change
  useEffect(() => {
    if (selectedOrganization) {
      fetchUsers();
    }
  }, [selectedOrganization, pagination.page, searchTerm, roleFilter]);

  const fetchUsers = async () => {
    if (!selectedOrganization) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getOrganizationUsers(selectedOrganization.id, {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        roleFilter: roleFilter || undefined
      });

      if (response.success && response.data) {
        setUsers(response.data);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setError(response.error?.message || 'Failed to fetch users');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleRoleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setRoleFilter(value ? parseInt(value) : null);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const getRoleBadgeColor = (roleId: number) => {
    switch (roleId) {
      case 1: return '#3b82f6'; // Student - Blue
      case 2: return '#10b981'; // Instructor - Green
      case 3: return '#f59e0b'; // Admin - Orange
      case 4: return '#ef4444'; // Super Admin - Red
      default: return '#6b7280'; // Unknown - Gray
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return '#10b981'; // Green
      case 'inactive': return '#6b7280'; // Gray
      case 'suspended': return '#ef4444'; // Red
      default: return '#6b7280';
    }
  };

  if (!selectedOrganization) {
    return (
      <div className="organization-users-page">
        <ErrorDisplay
          title="No Organization Selected"
          message="Please select an organization from the dashboard first."
          type="info"
        />
      </div>
    );
  }

  return (
    <div className="organization-users-page">
      {/* Header */}
      <div className="organization-users-header">
        <div>
          <h1>Organization Users</h1>
          <p>Manage users in {selectedOrganization.name}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="organization-users-filters">
        <div className="search-input-wrapper">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <FiFilter className="filter-icon" />
          <select value={roleFilter || ''} onChange={handleRoleFilterChange} className="filter-select">
            <option value="">All Roles</option>
            <option value="1">Student</option>
            <option value="2">Admin</option>
            <option value="3">Instructor</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="users-loading">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <ErrorDisplay
          title="Failed to Load Users"
          message={error}
          type="error"
          showRetry
          onRetry={fetchUsers}
        />
      ) : users.length === 0 ? (
        <div className="users-empty-state">
          <p>No users found</p>
          {(searchTerm || roleFilter) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setRoleFilter(null);
              }}
              className="clear-filters-btn"
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Reports To</th>
                  <th>Assigned</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-name-cell">
                        <div className="user-avatar">
                          {user.first_name?.[0] || user.email?.[0]?.toUpperCase()}
                          {user.last_name?.[0] || user.email?.[1]?.toUpperCase()}
                        </div>
                        <span>
                          {user.first_name || user.last_name
                            ? `${user.first_name} ${user.last_name}`.trim()
                            : user.email.split('@')[0]}
                        </span>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <span
                        className="role-badge"
                        style={{ backgroundColor: getRoleBadgeColor(user.role_id) }}
                      >
                        {user.role_name}
                      </span>
                    </td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getStatusBadgeColor(user.status) }}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => openReportsToPicker(user)}
                        title="Set who this user reports to"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          background: 'transparent',
                          border: '1px dashed #d1d5db',
                          borderRadius: 6,
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: 12,
                          color: user.reportsToName ? '#374151' : '#6b7280',
                        }}
                      >
                        <GitBranch size={12} />
                        {user.reportsToName || 'Set manager'}
                      </button>
                    </td>
                    <td>{new Date(user.assigned_at).toLocaleDateString()}</td>
                    <td>
                      <button
                        onClick={() => navigate(`/admin/users/${user.id}/activity`)}
                        title="View activity"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '6px 12px',
                          background: '#eff6ff',
                          color: '#2563eb',
                          border: '1px solid #bfdbfe',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: 500
                        }}
                      >
                        <FiActivity /> View activity
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="pagination-btn"
              >
                <FiChevronLeft />
                Previous
              </button>

              <div className="pagination-info">
                Page {pagination.page} of {pagination.totalPages}
                <span className="pagination-total">
                  ({pagination.total} total users)
                </span>
              </div>

              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="pagination-btn"
              >
                Next
                <FiChevronRight />
              </button>
            </div>
          )}
        </>
      )}

      {reportsToTarget && (
        <div
          onClick={closeReportsToPicker}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 8, width: 480, maxHeight: '80vh',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            }}
          >
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>
                  Set "Reports To" for{' '}
                  {reportsToTarget.first_name || reportsToTarget.last_name
                    ? `${reportsToTarget.first_name} ${reportsToTarget.last_name}`.trim()
                    : reportsToTarget.email}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                  Currently reporting to: {reportsToTarget.reportsToName || <em>nobody</em>}
                </div>
              </div>
              <button
                onClick={closeReportsToPicker}
                style={{ background: 'transparent', border: 0, cursor: 'pointer', padding: 4 }}
              >
                <FiX size={18} />
              </button>
            </div>

            <div style={{ padding: 12, borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ position: 'relative' }}>
                <LSearch size={14} style={{ position: 'absolute', left: 10, top: 9, color: '#9ca3af' }} />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={reportsToSearch}
                  onChange={(e) => setReportsToSearch(e.target.value)}
                  style={{
                    width: '100%', padding: '7px 10px 7px 28px',
                    border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13,
                  }}
                />
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 6 }}>
              {reportsToTarget.reportsToId && (
                <button
                  onClick={() => handlePickReportsTo(null, null)}
                  disabled={reportsToSaving}
                  style={{
                    width: '100%', textAlign: 'left', padding: '10px 12px',
                    background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
                    borderRadius: 6, marginBottom: 6, cursor: 'pointer', fontSize: 13,
                  }}
                >
                  Clear reports-to (no manager)
                </button>
              )}
              {reportsToLoading && <div style={{ padding: 16, color: '#6b7280' }}>Loading...</div>}
              {!reportsToLoading && reportsToCandidates.length === 0 && (
                <div style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>
                  {reportsToSearch ? 'No matches found.' : 'Type to search for a manager.'}
                </div>
              )}
              {reportsToCandidates.map((c) => (
                <button
                  key={c.id}
                  onClick={() => handlePickReportsTo(c.id, c.name)}
                  disabled={reportsToSaving}
                  style={{
                    width: '100%', textAlign: 'left', padding: '8px 10px',
                    background: '#fff', border: '1px solid #e5e7eb', color: '#111827',
                    borderRadius: 6, marginBottom: 4, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}
                >
                  <span
                    style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: '#e0e7ff', color: '#3730a3',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 600,
                    }}
                  >
                    {c.initials}
                  </span>
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</span>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>{c.email}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationUsers;
